import { useState, useEffect } from 'react';
import {
  Page,
  Card,
  Layout,
  Button,
  DataTable,
  Badge,
  Text,
  Modal,
  FormLayout,
  TextField,
  Select,
  EmptyState,
  Spinner,
  BlockStack,
} from '@shopify/polaris';
import {
  PlusMinor,
  DeleteMinor,
  EditMinor,
  ViewMajor,
  ExportMinor
} from '@shopify/polaris-icons';
import axios from 'axios';
import toast from 'react-hot-toast';

interface QRCode {
  id: string;
  name: string;
  description?: string;
  type: 'DIRECT' | 'PREFILLED' | 'CAMPAIGN';
  phoneNumber: string;
  prefilledMessage?: string;
  campaignId?: string;
  design: any;
  imageUrl?: string;
  format: string;
  scanCount: number;
  lastScannedAt?: string;
  isActive: boolean;
  createdAt: string;
}

export default function QRCodePage() {
  const [qrCodes, setQrCodes] = useState<QRCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingQRCode, setEditingQRCode] = useState<QRCode | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'DIRECT' as 'DIRECT' | 'PREFILLED' | 'CAMPAIGN',
    phoneNumber: '',
    prefilledMessage: '',
    design: {
      size: 512,
      foregroundColor: '#000000',
      backgroundColor: '#FFFFFF',
      errorCorrectionLevel: 'H' as 'L' | 'M' | 'Q' | 'H',
      format: 'png' as 'png' | 'svg'
    }
  });

  const storeId = localStorage.getItem('storeId') || '';
  const token = localStorage.getItem('token') || '';

  useEffect(() => {
    fetchQRCodes();
  }, []);

  const fetchQRCodes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/qr-codes`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setQrCodes(response.data.data);
    } catch (error) {
      console.error('Failed to fetch QR codes:', error);
      toast.error('Failed to load QR codes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingQRCode) {
        // Update existing QR code
        await axios.put(
          `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/qr-codes/${editingQRCode.id}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success('QR code updated successfully');
      } else {
        // Create new QR code
        await axios.post(
          `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/qr-codes`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success('QR code created successfully');
      }

      setShowModal(false);
      resetForm();
      fetchQRCodes();
    } catch (error: any) {
      console.error('Failed to save QR code:', error);
      toast.error(error.response?.data?.message || 'Failed to save QR code');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this QR code?')) return;

    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/qr-codes/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('QR code deleted successfully');
      fetchQRCodes();
    } catch (error) {
      console.error('Failed to delete QR code:', error);
      toast.error('Failed to delete QR code');
    }
  };

  const handlePreview = async (qrCode: QRCode) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/qr-codes/${qrCode.id}/preview`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setPreviewData(response.data.data);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Failed to load preview:', error);
      toast.error('Failed to load preview');
    }
  };

  const handleDownload = async (qrCode: QRCode) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/stores/${storeId}/qr-codes/${qrCode.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `qr-code-${qrCode.name}.${qrCode.format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('QR code downloaded');
    } catch (error) {
      console.error('Failed to download QR code:', error);
      toast.error('Failed to download QR code');
    }
  };

  const handleEdit = (qrCode: QRCode) => {
    setEditingQRCode(qrCode);
    setFormData({
      name: qrCode.name,
      description: qrCode.description || '',
      type: qrCode.type,
      phoneNumber: qrCode.phoneNumber,
      prefilledMessage: qrCode.prefilledMessage || '',
      design: qrCode.design || formData.design
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'DIRECT',
      phoneNumber: '',
      prefilledMessage: '',
      design: {
        size: 512,
        foregroundColor: '#000000',
        backgroundColor: '#FFFFFF',
        errorCorrectionLevel: 'H',
        format: 'png'
      }
    });
    setEditingQRCode(null);
  };

  const typeOptions = [
    { label: 'Direct Link', value: 'DIRECT' },
    { label: 'Pre-filled Message', value: 'PREFILLED' },
    { label: 'Campaign Link', value: 'CAMPAIGN' }
  ];

  const errorCorrectionOptions = [
    { label: 'Low (7%)', value: 'L' },
    { label: 'Medium (15%)', value: 'M' },
    { label: 'Quartile (25%)', value: 'Q' },
    { label: 'High (30%)', value: 'H' }
  ];

  const formatOptions = [
    { label: 'PNG', value: 'png' },
    { label: 'SVG', value: 'svg' }
  ];

  const rows = (qrCodes || []).map((qrCode) => [
    qrCode.name,
    <Badge tone={qrCode.type === 'DIRECT' ? 'info' : qrCode.type === 'PREFILLED' ? 'warning' : 'success'}>
      {qrCode.type}
    </Badge>,
    qrCode.phoneNumber,
    qrCode.scanCount.toString(),
    <Badge tone={qrCode.isActive ? 'success' : 'critical'}>
      {qrCode.isActive ? 'Active' : 'Inactive'}
    </Badge>,
    <div style={{ display: 'flex', gap: '8px' }}>
      <Button
        icon={ViewMajor}
        onClick={() => handlePreview(qrCode)}
        size="slim"
      />
      <Button
        icon={ExportMinor}
        onClick={() => handleDownload(qrCode)}
        size="slim"
      />
      <Button
        icon={EditMinor}
        onClick={() => handleEdit(qrCode)}
        size="slim"
      />
      <Button
        icon={DeleteMinor}
        onClick={() => handleDelete(qrCode.id)}
        tone="critical"
        size="slim"
      />
    </div>
  ]);

  const modalContent = (
    <Modal
      open={showModal}
      onClose={() => {
        setShowModal(false);
        resetForm();
      }}
      title={editingQRCode ? 'Edit QR Code' : 'Create QR Code'}
      primaryAction={{
        content: editingQRCode ? 'Update' : 'Create',
        onAction: handleSubmit
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: () => {
            setShowModal(false);
            resetForm();
          }
        }
      ]}
    >
      <Modal.Section>
        <FormLayout>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(value) => setFormData({ ...formData, name: value })}
            placeholder="e.g., Main WhatsApp Contact"
            autoComplete="off"
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(value) => setFormData({ ...formData, description: value })}
            placeholder="Optional description"
            multiline={3}
            autoComplete="off"
          />

          <Select
            label="Type"
            options={typeOptions}
            value={formData.type}
            onChange={(value) => setFormData({ ...formData, type: value as any })}
          />

          <TextField
            label="Phone Number"
            value={formData.phoneNumber}
            onChange={(value) => setFormData({ ...formData, phoneNumber: value })}
            placeholder="+1234567890"
            helpText="Include country code (e.g., +1 for USA)"
            autoComplete="off"
          />

          {formData.type === 'PREFILLED' && (
            <TextField
              label="Pre-filled Message"
              value={formData.prefilledMessage}
              onChange={(value) => setFormData({ ...formData, prefilledMessage: value })}
              placeholder="Hello! I'd like to know more about..."
              multiline={4}
              autoComplete="off"
            />
          )}

          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h3">QR Code Design</Text>
              <FormLayout>
                <TextField
                  label="Size (pixels)"
                  value={formData.design.size.toString()}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      design: { ...formData.design, size: parseInt(value) || 512 }
                    })
                  }
                  type="number"
                  autoComplete="off"
                />

                <TextField
                  label="Foreground Color"
                  value={formData.design.foregroundColor}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      design: { ...formData.design, foregroundColor: value }
                    })
                  }
                  placeholder="#000000"
                  autoComplete="off"
                />

                <TextField
                  label="Background Color"
                  value={formData.design.backgroundColor}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      design: { ...formData.design, backgroundColor: value }
                    })
                  }
                  placeholder="#FFFFFF"
                  autoComplete="off"
                />

                <Select
                  label="Error Correction Level"
                  options={errorCorrectionOptions}
                  value={formData.design.errorCorrectionLevel}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      design: { ...formData.design, errorCorrectionLevel: value as any }
                    })
                  }
                />

                <Select
                  label="Format"
                  options={formatOptions}
                  value={formData.design.format}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      design: { ...formData.design, format: value as any }
                    })
                  }
                />
              </FormLayout>
            </BlockStack>
          </Card>
        </FormLayout>
      </Modal.Section>
    </Modal>
  );

  const previewModalContent = (
    <Modal
      open={showPreviewModal}
      onClose={() => setShowPreviewModal(false)}
      title="QR Code Preview"
    >
      <Modal.Section>
        {previewData && (
          <div style={{ textAlign: 'center' }}>
            <img
              src={previewData.dataURL}
              alt="QR Code Preview"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
            <div style={{ marginTop: '16px' }}>
              <Text as="p" variant="bodyMd">
                <strong>Link:</strong> {previewData.link}
              </Text>
            </div>
          </div>
        )}
      </Modal.Section>
    </Modal>
  );

  if (loading) {
    return (
      <Page title="QR Codes">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="QR Codes"
      primaryAction={{
        content: 'Create QR Code',
        icon: PlusMinor,
        onAction: () => setShowModal(true)
      }}
    >
        <Layout>
          <Layout.Section>
            {!qrCodes || qrCodes.length === 0 ? (
              <Card>
                <EmptyState
                  heading="Create your first QR code"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{
                    content: 'Create QR Code',
                    onAction: () => setShowModal(true)
                  }}
                >
                  <p>
                    Generate WhatsApp QR codes to make it easy for customers to contact you.
                    Print them on flyers, business cards, or share them digitally.
                  </p>
                </EmptyState>
              </Card>
            ) : (
              <Card>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'text']}
                  headings={['Name', 'Type', 'Phone Number', 'Scans', 'Status', 'Actions']}
                  rows={rows}
                />
              </Card>
            )}
          </Layout.Section>

          {modalContent}
          {previewModalContent}
        </Layout>
      </Page>
    );
  }
